// /app/frontend/src/pages/admin/AdminRoomReportsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, AlertTriangle, CheckCircle, XCircle, Trash2 } from "lucide-react";
import { listRoomReports, updateRoomReport, deleteRoomPost, deleteRoomReply } from "../../api/adminRoomReports";

function timeLabel(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso || "";
  }
}

const STATUS_COLORS = {
  open: "bg-amber-100 text-amber-800",
  resolved: "bg-green-100 text-green-800",
  ignored: "bg-gray-100 text-gray-600",
};

const STATUS_ICONS = {
  open: AlertTriangle,
  resolved: CheckCircle,
  ignored: XCircle,
};

export default function AdminRoomReportsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("open");
  const [data, setData] = useState({ items: [], total: 0, limit: 50, skip: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async (opts = {}) => {
    setLoading(true);
    setErr("");
    try {
      const res = await listRoomReports({
        status,
        limit: data.limit,
        skip: data.skip,
        ...opts,
      });
      setData(res);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to load room reports.";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load({ skip: 0 });
  }, [status]);

  const items = useMemo(() => (Array.isArray(data.items) ? data.items : []), [data.items]);

  const openCount = useMemo(() => {
    if (status === "open") return data.total;
    return null;
  }, [status, data.total]);

  return (
    <div className="min-h-screen surface-page">
      <div className="mx-auto w-full max-w-5xl p-4">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="rounded-xl p-2 hover:bg-gray-200 dark:hover:bg-slate-800 transition"
              onClick={() => navigate("/admin")}
            >
              <ArrowLeft className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-rose-500" />
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Room Reports</h1>
                {openCount !== null && openCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-xs font-medium">
                    {openCount} open
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Moderation queue for Rooms posts & replies</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <select
              className="rounded-xl border border-gray-200 dark:border-slate-600 p-2.5 text-sm surface-input"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={loading}
            >
              <option value="open">Open</option>
              <option value="resolved">Resolved</option>
              <option value="ignored">Ignored</option>
              <option value="all">All</option>
            </select>

            <button
              className="rounded-xl border border-gray-200 dark:border-slate-700 surface-card px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-slate-800 transition"
              onClick={() => load()}
              disabled={loading}
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {err && (
          <div className="mb-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-sm text-red-700 dark:text-red-300">
            {err}
          </div>
        )}

        {/* Reports List */}
        <div className="rounded-2xl surface-card shadow-sm overflow-hidden">
          <div className="flex items-center justify-between border-b border-gray-100 dark:border-slate-700 px-4 py-3">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Reports Queue</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Total: {data.total} • Showing: {items.length}
            </div>
          </div>

          {loading && items.length === 0 ? (
            <div className="p-8 text-sm text-gray-500 text-center">Loading reports…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-sm text-gray-500 text-center">
              No {status === "all" ? "" : status} reports found.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map((r) => (
                <ReportRow key={r.id} report={r} onActionDone={load} />
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="mt-4 flex items-center justify-between">
          <button
            className="rounded-xl surface-card px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50 transition"
            disabled={loading || (data.skip || 0) <= 0}
            onClick={() => load({ skip: Math.max(0, (data.skip || 0) - (data.limit || 50)) })}
          >
            ← Previous
          </button>

          <div className="text-xs text-gray-500 dark:text-gray-400">
            Page {Math.floor((data.skip || 0) / (data.limit || 50)) + 1} of{" "}
            {Math.ceil((data.total || 1) / (data.limit || 50))}
          </div>

          <button
            className="rounded-xl surface-card px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50 transition"
            disabled={loading || (data.skip || 0) + (data.limit || 50) >= (data.total || 0)}
            onClick={() => load({ skip: (data.skip || 0) + (data.limit || 50) })}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}

function ReportRow({ report, onActionDone }) {
  const [note, setNote] = useState(report.admin_note || "");
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState("");

  const StatusIcon = STATUS_ICONS[report.status] || AlertTriangle;

  const doStatus = async (status) => {
    setBusy(true);
    setLocalErr("");
    try {
      await updateRoomReport(report.id, { status, admin_note: note });
      await onActionDone?.();
    } catch (e) {
      setLocalErr(e?.response?.data?.detail || e?.message || "Action failed.");
    } finally {
      setBusy(false);
    }
  };

  const doTakedown = async () => {
    if (!window.confirm("This will permanently delete the reported content. Continue?")) return;
    
    setBusy(true);
    setLocalErr("");
    try {
      if (report.target_type === "post") await deleteRoomPost(report.target_id);
      if (report.target_type === "reply") await deleteRoomReply(report.target_id);
      // After takedown, resolve the report
      await updateRoomReport(report.id, { status: "resolved", admin_note: note || "Content removed by admin." });
      await onActionDone?.();
    } catch (e) {
      setLocalErr(e?.response?.data?.detail || e?.message || "Takedown failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 hover:bg-gray-50 dark:hover:bg-slate-800 transition">
      <div className="flex flex-wrap items-start justify-between gap-4">
        {/* Report Info */}
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[report.status] || STATUS_COLORS.open}`}>
              <StatusIcon className="w-3 h-3 inline mr-1" />
              {report.status?.toUpperCase()}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 text-xs font-medium">
              {report.target_type?.toUpperCase()}
            </span>
          </div>

          <div className="text-sm">
            <span className="font-medium text-gray-900 dark:text-gray-100">Reason:</span>{" "}
            <span className="text-gray-700 dark:text-gray-300">{report.reason}</span>
          </div>

          {report.details && (
            <div className="mt-2 text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap bg-gray-50 dark:bg-slate-800 rounded-lg p-2">
              {report.details}
            </div>
          )}

          <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 space-y-1">
            <div>Reported: {timeLabel(report.created_at)}</div>
            <div>Target ID: <code className="bg-gray-100 dark:bg-slate-700 px-1 rounded">{report.target_id}</code></div>
            <div>Reporter: <code className="bg-gray-100 dark:bg-slate-700 px-1 rounded">{report.reported_by}</code></div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 min-w-[280px]">
          <textarea
            className="h-20 w-full resize-none rounded-xl border border-gray-200 p-3 text-sm outline-none focus:border-gray-300 transition"
            placeholder="Admin note (optional)…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy}
          />

          {localErr && <div className="text-xs text-red-600">{localErr}</div>}

          <div className="flex flex-wrap gap-2 justify-end">
            <button
              className="rounded-xl border border-gray-200 px-3 py-2 text-xs hover:bg-gray-100 disabled:opacity-50 transition"
              onClick={() => doStatus("ignored")}
              disabled={busy}
            >
              <XCircle className="w-3.5 h-3.5 inline mr-1" />
              Ignore
            </button>
            <button
              className="rounded-xl border border-gray-200 px-3 py-2 text-xs hover:bg-gray-100 disabled:opacity-50 transition"
              onClick={() => doStatus("resolved")}
              disabled={busy}
            >
              <CheckCircle className="w-3.5 h-3.5 inline mr-1" />
              Resolve
            </button>
            <button
              className="rounded-xl bg-red-500 hover:bg-red-600 px-3 py-2 text-xs text-white disabled:opacity-50 transition"
              onClick={doTakedown}
              disabled={busy}
              title="Deletes the reported content, then resolves the report"
            >
              <Trash2 className="w-3.5 h-3.5 inline mr-1" />
              {busy ? "Working…" : "Remove"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
